/**
 * Agile Delivery types.
 *
 * Part of the Hybrid Waterfall/Agile Delivery feature. Mirrors the DB shapes
 * from migrations 00445 / 00446 and exports the enumerated unions used to
 * drive validated selects (standing rule: enumerated fields render as selects,
 * so the option lists and the types are driven from one place here).
 *
 * Vocabulary is generic Scrum / SAFe terminology (Epic, Feature, Story,
 * Sprint, Backlog, Board, Program Increment) - methodology terms, never a
 * commercial product name.
 */

// ── Enumerated unions (single source for types + select option lists) ──────

export const WORK_ITEM_TYPES = ['epic', 'feature', 'story', 'task', 'bug', 'spike'] as const
export type WorkItemType = (typeof WORK_ITEM_TYPES)[number]

export const PRIORITIES = ['critical', 'high', 'medium', 'low'] as const
export type Priority = (typeof PRIORITIES)[number]

export const MOSCOW = ['must', 'should', 'could', 'wont'] as const
export type Moscow = (typeof MOSCOW)[number]

export const STATUS_CATEGORIES = ['todo', 'in_progress', 'done'] as const
export type StatusCategory = (typeof STATUS_CATEGORIES)[number]

export const EV_CREDIT_RULES = ['zero_hundred', 'fifty_fifty', 'percent_complete'] as const
export type EvCreditRule = (typeof EV_CREDIT_RULES)[number]

export const DELIVERY_METHODOLOGIES = ['waterfall', 'agile', 'hybrid'] as const
export type DeliveryMethodology = (typeof DELIVERY_METHODOLOGIES)[number]

export const LINK_TYPES = ['blocks', 'blocked_by', 'relates_to', 'duplicates', 'depends_on'] as const
export type LinkType = (typeof LINK_TYPES)[number]

export const BOARD_TYPES = ['scrum', 'kanban'] as const
export type BoardType = (typeof BOARD_TYPES)[number]

export const SWIMLANE_BY = ['none', 'epic', 'assignee', 'priority', 'expedite'] as const
export type SwimlaneBy = (typeof SWIMLANE_BY)[number]

export const SPRINT_STATES = ['planned', 'active', 'closed'] as const
export type SprintState = (typeof SPRINT_STATES)[number]

export const PI_STATES = ['planned', 'active', 'closed'] as const
export type PiState = (typeof PI_STATES)[number]

/** Fibonacci story-point scale used by the inline picker and Planning Poker. */
export const STORY_POINT_SCALE = [1, 2, 3, 5, 8, 13, 21] as const

// ── Label maps (human-readable copy for the selects / badges) ──────────────

export const WORK_ITEM_TYPE_LABELS: Record<WorkItemType, string> = {
  epic: 'Epic',
  feature: 'Feature',
  story: 'Story',
  task: 'Task',
  bug: 'Bug',
  spike: 'Spike',
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const MOSCOW_LABELS: Record<Moscow, string> = {
  must: 'Must have',
  should: 'Should have',
  could: 'Could have',
  wont: "Won't have (this time)",
}

export const STATUS_CATEGORY_LABELS: Record<StatusCategory, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
}

export const EV_CREDIT_RULE_LABELS: Record<EvCreditRule, string> = {
  zero_hundred: '0 / 100 (credit only when Done)',
  fifty_fifty: '50 / 50 (half on start, half on done)',
  percent_complete: 'Percent complete',
}

export const DELIVERY_METHODOLOGY_LABELS: Record<DeliveryMethodology, string> = {
  waterfall: 'Waterfall',
  agile: 'Agile',
  hybrid: 'Hybrid',
}

export const LINK_TYPE_LABELS: Record<LinkType, string> = {
  blocks: 'Blocks',
  blocked_by: 'Blocked by',
  relates_to: 'Relates to',
  duplicates: 'Duplicates',
  depends_on: 'Depends on',
}

/** The item types that can parent another type, top to bottom of the hierarchy. */
export const HIERARCHY_ORDER: WorkItemType[] = ['epic', 'feature', 'story', 'task', 'bug', 'spike']

// ── Nested value shapes ────────────────────────────────────────────────────

export interface AcceptanceCriterion {
  id: string
  text: string
  met: boolean
}

export interface DodItem {
  id: string
  text: string
  done: boolean
}

// ── Core entities (camelCase, mirror the DB rows) ──────────────────────────

export interface WorkItem {
  id: string
  projectId: string
  seq: number
  /** Human-readable key, derived in the mapper from project.code + seq. */
  key: string
  itemType: WorkItemType
  title: string
  description?: string
  status: string
  statusCategory: StatusCategory
  priority: Priority
  moscow?: Moscow
  parentId?: string
  storyPoints?: number
  estimateHours?: number
  remainingHours?: number
  loggedHours?: number
  backlogRank?: string
  boardId?: string
  sprintId?: string
  programIncrementId?: string
  assigneeId?: string
  assigneeName?: string
  reporterId?: string
  labels: string[]
  acceptanceCriteria: AcceptanceCriterion[]
  dodChecklist: DodItem[]
  wbsElementId?: string
  controlAccountId?: string
  /** Linked schedule activity (schedule_activities.id). Set on a Feature to bind
   *  it to a schedule task. The activity's early start/finish define the
   *  constraint window its child stories must sit within. */
  scheduleActivityId?: string
  /** The schedule version the linked activity belongs to (activity ids are only
   *  unique within a version), so the link resolves back cleanly. */
  scheduleVersionId?: string
  /** Constraint window, cached from the linked activity's early start/finish at
   *  link time. On a Feature this is the linked task's window; a story resolves
   *  its window from the nearest ancestor Feature that has one. */
  constraintStart?: string
  constraintFinish?: string
  evCreditRule: EvCreditRule
  /** Level-specific standard fields (work_items.attributes, see components/agile/attributes.ts). */
  attributes: Record<string, unknown>
  percentComplete: number
  blocked: boolean
  blockedReason?: string
  startDate?: string
  dueDate?: string
  completedAt?: string
  dataClass: 'demo' | 'production'
  createdAt: string
  updatedAt: string
}

export interface BoardColumn {
  id: string
  boardId: string
  name: string
  statusKey: string
  statusCategory: StatusCategory
  wipLimit?: number
  columnOrder: number
}

export interface Board {
  id: string
  projectId: string
  name: string
  boardType: BoardType
  swimlaneBy: SwimlaneBy
  isDefault: boolean
  columns: BoardColumn[]
}

export interface SprintCapacity {
  id: string
  sprintId: string
  resourceId?: string
  memberName: string
  capacityHours: number
}

export interface Sprint {
  id: string
  projectId: string
  boardId?: string
  programIncrementId?: string
  name: string
  goal?: string
  state: SprintState
  startDate?: string
  endDate?: string
  committedPoints?: number
  completedPoints?: number
  reviewNotes?: string
  retroNotes?: string
  capacity?: SprintCapacity[]
}

export interface ProgramIncrement {
  id: string
  projectId: string
  name: string
  objective?: string
  startDate?: string
  endDate?: string
  milestoneId?: string
  wbsElementId?: string
  state: PiState
  /**
   * Lifecycle phase-gate target. When the PI is closed, the parent project's
   * lifecycle phase is advanced to this phase name (reusing the same
   * updateProjectPhase hook a schedule milestone's lifecyclePhaseTransition
   * uses). null / undefined = closing the PI does not advance a phase gate.
   * Persisted in `agile_program_increments.lifecycle_phase_transition`
   * (migration 00448). Part of the hybrid bridge (Section 2.3 / 9.8).
   */
  lifecyclePhaseTransition?: string | null
}

export interface WorkItemLink {
  id: string
  sourceId: string
  targetId: string
  linkType: LinkType
}

export interface WorkItemComment {
  id: string
  workItemId: string
  authorId?: string
  authorName?: string
  body: string
  createdAt: string
}

export interface WorkItemActivity {
  id: string
  workItemId: string
  actorId?: string
  actorName?: string
  action: 'created' | 'field_changed' | 'moved' | 'commented' | 'linked'
  field?: string
  fromValue?: string
  toValue?: string
  createdAt: string
}

export interface AgileMetricSnapshot {
  id: string
  projectId: string
  sprintId?: string
  boardId?: string
  snapshotDate: string
  remainingPoints?: number
  completedPoints?: number
  scopePoints?: number
  remainingHours?: number
  cfdCounts?: Record<string, number>
  dataClass: 'demo' | 'production'
}

// ── Backlog filter shape (store slice) ─────────────────────────────────────

export interface BacklogFilters {
  types: WorkItemType[]
  labels: string[]
  assignee: string | null
  epicId: string | null
  text: string
  /** Quick filters: only-unestimated, only-blocked, only-ready. */
  quick: 'all' | 'unestimated' | 'blocked' | 'ready'
  /** Status keys (any of). Optional so presets saved before it still load. */
  statuses?: string[]
  /** Priorities (any of). */
  priorities?: Priority[]
  /** Sprint id, or '__none__' for items not in any sprint. */
  sprint?: string | null
  /** Tag match mode when several tags are picked. */
  labelMode?: 'any' | 'all'
}

export const DEFAULT_BACKLOG_FILTERS: BacklogFilters = {
  types: [],
  labels: [],
  assignee: null,
  epicId: null,
  text: '',
  quick: 'all',
}

/**
 * A named, reusable snapshot of the backlog filter bar. Stored per project in
 * the app store (persisted to localStorage), so a team's saved views survive a
 * reload. The `filters` payload is a full BacklogFilters value.
 */
export interface FilterPreset {
  id: string
  name: string
  filters: BacklogFilters
}

/** True when the filter set is doing any narrowing (drives "Clear" affordances). */
export function isBacklogFilterActive(f: BacklogFilters): boolean {
  return (
    f.types.length > 0 ||
    f.labels.length > 0 ||
    f.assignee !== null ||
    f.epicId !== null ||
    f.text.trim() !== '' ||
    f.quick !== 'all' ||
    (f.statuses?.length ?? 0) > 0 ||
    (f.priorities?.length ?? 0) > 0 ||
    !!f.sprint
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Derive the human-readable work-item key, e.g. `PRJ-2025-001-45`. */
export function deriveWorkItemKey(projectCode: string | undefined, seq: number): string {
  const base = (projectCode ?? 'WI').trim() || 'WI'
  return `${base}-${seq}`
}

/**
 * Definition of Ready: a story is ready when it is estimated, has at least one
 * acceptance criterion, and is not blocked. Epics/Features are exempt (they
 * refine into stories) and always considered ready for planning purposes.
 */
export function isDefinitionOfReady(item: WorkItem): boolean {
  if (item.itemType === 'epic' || item.itemType === 'feature') return true
  const estimated = typeof item.storyPoints === 'number' && item.storyPoints > 0
  const hasCriteria = item.acceptanceCriteria.length > 0
  return estimated && hasCriteria && !item.blocked
}

/** Convert an enumerated union to SearchableSelect option objects. */
export function toSelectOptions<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): { value: T; label: string }[] {
  return values.map((v) => ({ value: v, label: labels[v] }))
}
